import React, { useEffect, useState } from 'react';
import {
  Box,
  HeadingText,
  GDSStyleObject,
  FormField,
  TextInput,
  Select,
  StackLayout,
  UtilityText,
} from '@leagueplatform/genesis-core';
import {
  memberNameOption,
  phoneNumberOption,
  formatPhoneNumber,
  isValidatePhoneLength,
  isEmpty,
  isNotEmpty,
  updateFormData,
  isNoSpace,
  formatDate,
} from '../../../helpers';
import { PHONE_NUMBER_TYPE } from '../../../../../types/online-forms.types';

export function MemberInformation(props: any) {
  const { errorHandling, nextButtonClicked, profileResponseData } = props;

  const [otherPhNumError, setOtherPhNumError] = useState<string>('');
  const [isOtherPhone, setIsOtherPhone] = useState<boolean>(false);
  const [form, setForm] = useState({
    memberName: '',
    memberId: profileResponseData?.demographics?.memberId,
    dateOfBirth: formatDate(profileResponseData?.demographics?.dateOfBirth),
    phoneNumber: '',
    otherPhoneNumber: '',
    dependents: [],
  });
  const [errors, setErrors] = useState({
    otherPhoneNumber: false,
  });

  const formField =
    isEmpty(form.otherPhoneNumber) ||
    isValidatePhoneLength(form.otherPhoneNumber);

  const isValid = isNotEmpty(form.otherPhoneNumber);

  useEffect(() => {
    if (formField && isOtherPhone) {
      errorHandling(false);
      if (isEmpty(form.otherPhoneNumber)) {
        setOtherPhNumError(
          'Your phone number is required to process this request.',
        );
      } else if (isValidatePhoneLength(form.otherPhoneNumber)) {
        setOtherPhNumError('Enter a valid phone number.');
      } else {
        setOtherPhNumError('');
      }
    } else {
      errorHandling(true);
    }
  }, [formField, isOtherPhone, errorHandling, form.otherPhoneNumber]);

  useEffect(() => {
    if (nextButtonClicked) {
      if (!isValid) {
        setErrors({
          otherPhoneNumber: isEmpty(form.otherPhoneNumber),
        });
      }
    }
  }, [nextButtonClicked, isValid, form.otherPhoneNumber]);

  const updateErrorState = (value: string, id: string) => {
    if (id === 'otherPhoneNumber') {
      if (isEmpty(form.otherPhoneNumber)) {
        setOtherPhNumError(
          'Your phone number is required to process this request.',
        );
        setErrors((prevState) => ({
          ...prevState,
          otherPhoneNumber: true,
        }));
      } else if (isValidatePhoneLength(value)) {
        setOtherPhNumError('Enter a valid phone number.');
        setErrors((prevState) => ({
          ...prevState,
          otherPhoneNumber: true,
        }));
      } else {
        setOtherPhNumError('');
        setErrors((prevState) => ({
          ...prevState,
          otherPhoneNumber: false,
        }));
      }
    }
  };

  const onUpdateField = (event: any) => {
    const { value, id } = event.target;
    const nextFormState = {
      ...form,
      [id]: value,
    };
    setForm(nextFormState);
    updateErrorState(value, id);
    if (id === 'otherPhoneNumber') {
      setForm((prevState) => ({
        ...prevState,
        otherPhoneNumber: isNoSpace(value) ? '' : formatPhoneNumber(value),
      }));
    }
    if (id === 'phoneNumber') {
      setIsOtherPhone(event.target.value === PHONE_NUMBER_TYPE.OTHERS);
      if (event.target.value !== PHONE_NUMBER_TYPE.OTHERS) {
        setForm((prevState) => ({
          ...prevState,
          otherPhoneNumber: '',
        }));
        setErrors((prevState) => ({
          ...prevState,
          otherPhoneNumber: false,
        }));
      }
    }
  };

  useEffect(() => {
    if (
      form.memberName &&
      profileResponseData?.demographics?.dependents !== null
    ) {
      const dependentsUser = profileResponseData?.demographics?.dependents.map(
        (option: any) => ({
          name: `${option.firstName} ${option.lastName}`,
          dateOfBirth: formatDate(option.dateOfBirth),
        }),
      );

      const mainUser = {
        name: `${profileResponseData?.demographics?.firstName} ${profileResponseData?.demographics?.lastName}`,
        dateOfBirth: formatDate(profileResponseData?.demographics?.dateOfBirth),
      };
      dependentsUser?.unshift(mainUser);
      const selectedUser = dependentsUser?.find(
        (o: any) => o.name === form.memberName,
      );

      setForm((prevState) => ({
        ...prevState,
        dateOfBirth: selectedUser.dateOfBirth,
        dependents: dependentsUser,
      }));
    }
  }, [form.memberName, profileResponseData]);

  useEffect(() => {
    const memberInfo = JSON.parse(sessionStorage.getItem('formData') || '{}');
    if (memberInfo) {
      setForm({
        memberName: memberInfo?.memberName
          ? memberInfo?.memberName
          : `${profileResponseData?.demographics?.firstName} ${profileResponseData?.demographics?.lastName}`,
        memberId: memberInfo?.memberId
          ? memberInfo?.memberId
          : profileResponseData?.demographics?.memberId,
        dateOfBirth:
          memberInfo?.dateOfBirth ||
          formatDate(profileResponseData?.demographics?.dateOfBirth),
        phoneNumber: memberInfo?.phoneNumber
          ? memberInfo?.phoneNumber
          : phoneNumberOption(profileResponseData)[0]?.value,
        otherPhoneNumber: memberInfo?.otherPhoneNumber,
        dependents: memberInfo?.dependents,
      });
    }
  }, [profileResponseData]);

  useEffect(() => {
    if (form) {
      updateFormData(form);
    }
  }, [form]);

  useEffect(() => {
    if (form.phoneNumber === PHONE_NUMBER_TYPE.OTHERS) {
      setIsOtherPhone(true);
    } else {
      setIsOtherPhone(false);
    }
  }, [form]);

  const onBlurField = (event: any) => {
    updateErrorState(event.target.value, event.target.id);
  };

  const onKeyPressField = (event: any) => {
    updateErrorState(event.target.value, event.target.id);
  };

  return (
    <StackLayout
      horizontalAlignment="center"
      data-testid="exception-request-form"
      css={
        {
          backgroundColor: 'White',
          maxWidth: '800px',
        } as GDSStyleObject
      }
    >
      <Box
        css={
          {
            display: 'flex',
            flexDirection: 'column',
            padding: '$three $four',
          } as GDSStyleObject
        }
      >
        <HeadingText
          level="1"
          size={{
            '@initial': 'xxl',
            '@mobile': 'lg',
            '@mobileLandscape': 'lg',
            '@tablet': 'xl',
            '@laptop': 'xxl',
            '@desktop': 'xxl',
          }}
        >
          Medication Exception Request Form
        </HeadingText>
      </Box>

      <Box
        css={
          {
            display: 'flex',
            flexDirection: 'column',
            padding: '$none $two',

            width: '100%',
          } as GDSStyleObject
        }
      >
        <Box
          css={
            {
              backgroundColor: '#F8F8F8',
              padding: '$one $two',
            } as GDSStyleObject
          }
        >
          <HeadingText
            level="3"
            size={{
              '@initial': 'xl',
              '@mobile': 'lg',
              '@mobileLandscape': 'lg',
              '@tablet': 'xl',
              '@laptop': 'xl',
              '@desktop': 'xl',
            }}
          >
            Form Instructions:
          </HeadingText>
          <Box
            css={
              {
                marginTop: '$one',
                paddingLeft: '$one',
              } as GDSStyleObject
            }
          >
            <UtilityText>
              <ol>
                <li key="1">1. Complete all required fields to submit.</li>
                <li key="2">
                  2. Enter full name and address (including city, state, and zip
                  code)
                  <br /> when a name and address field is required.
                </li>
                <li key="3">
                  3. All fields are required unless marked optional.
                </li>
              </ol>
            </UtilityText>
          </Box>
        </Box>
      </Box>
      <Box
        css={
          {
            display: 'flex',
            flexDirection: 'column',
            padding: '$two $four $three $four',
            width: '100%',
          } as GDSStyleObject
        }
      >
        <Box
          css={
            {
              display: 'flex',
            } as GDSStyleObject
          }
        >
          <HeadingText
            level="3"
            size={{
              '@initial': 'xl',
              '@mobile': 'lg',
              '@mobileLandscape': 'lg',
              '@tablet': 'xl',
              '@laptop': 'xl',
              '@desktop': 'xl',
            }}
          >
            Step 1: Member Information
          </HeadingText>
        </Box>
        <Box>
          <Box
            css={
              {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '$one $none $none $one',
              } as GDSStyleObject
            }
          >
            {profileResponseData?.demographics?.dependents ||
            form.dependents ? (
              <FormField id="memberName" label="Member Name">
                <Select
                  options={memberNameOption(profileResponseData)}
                  onChange={onUpdateField}
                  value={form.memberName}
                />
              </FormField>
            ) : (
              <FormField id="memberName" label="Member Name">
                <TextInput
                  disabled
                  value={`${profileResponseData?.demographics?.firstName} ${profileResponseData?.demographics?.lastName}`}
                />
              </FormField>
            )}
          </Box>
          <Box
            css={
              {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '$one $none $none $one',
              } as GDSStyleObject
            }
          >
            <FormField id="memberId" label="Member ID">
              <TextInput disabled value={form.memberId} />
            </FormField>
          </Box>
          <Box
            css={
              {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '$one $none $none $one',
              } as GDSStyleObject
            }
          >
            <FormField id="dateOfBirth" label="Date of Birth (MM/DD/YYYY)">
              <TextInput disabled value={form.dateOfBirth} />
            </FormField>
          </Box>
          <Box
            css={
              {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '$one $none $none $one',
              } as GDSStyleObject
            }
          >
            <FormField id="phoneNumber" label="Phone Number">
              <Select
                options={phoneNumberOption(profileResponseData)}
                onChange={onUpdateField}
                value={form.phoneNumber}
              />
            </FormField>
          </Box>
          {isOtherPhone && (
            <Box
              css={
                {
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '$one $none $none $one',
                } as GDSStyleObject
              }
            >
              <FormField
                id="otherPhoneNumber"
                label="Other Phone Number"
                inputStatus={errors.otherPhoneNumber ? 'error' : undefined}
                statusIconLabel={errors.otherPhoneNumber ? 'Error' : ''}
                statusMessage={errors.otherPhoneNumber ? otherPhNumError : ''}
              >
                <TextInput
                  onChange={onUpdateField}
                  value={formatPhoneNumber(form.otherPhoneNumber)}
                  maxLength={14}
                  onBlur={onBlurField}
                  onKeyPress={onKeyPressField}
                />
              </FormField>
            </Box>
          )}
        </Box>
      </Box>
    </StackLayout>
  );
}
